import CHARGES_SOCIALES from '../constants/charges-sociales.json';
import { cantonDeLaSociete } from './canton';

/**
 * Taux des charges sociales suisses, année par année.
 *
 * Le barème vivait en double, dans l'écran de l'équipe et dans celui des
 * réglages. Deux copies d'un même chiffre finissent par diverger : l'une des
 * deux serait mise à jour lors d'un changement de taux, l'autre non, et la
 * fiche de paie ne dirait plus la même chose selon l'écran qui l'a calculée.
 *
 * Les taux changent d'une année à l'autre (AVS, AC, primes LAA, allocations
 * familiales…). Un seul jeu de taux obligeait à choisir entre fausser les
 * fiches de l'an passé et fausser celles de cette année : la société règle
 * désormais un jeu par année, et chaque fiche lit celui de sa période. Le
 * serveur applique la même règle (`TauxParAnnee`).
 */

/**
 * Année du barème d'où viennent les taux par défaut.
 *
 * C'est elle qui s'affiche sous le tableau, et non l'année en cours : la
 * mention vieillissait en silence et finissait par présenter des taux de l'an
 * passé comme ceux de cette année. C'est aussi l'année sous laquelle est lu un
 * ancien réglage `soc.taux`, écrit avant que les taux ne soient réglés par
 * année : il datait de ce barème, et les sociétés qui l'avaient saisi ne
 * doivent rien perdre.
 */
export const ANNEE_BAREME = 2025;

/** Taux par défaut, en pourcentage du salaire brut. */
export const TAUX_DEFAUT = Object.freeze({
  avsSal: 5.3,
  acSal: 1.1,
  aanpSal: 1.4,
  ijmSal: 0,
  avsPat: 5.3,
  acPat: 1.1,
  aapPat: 0.7,
  afPat: 1.8,
  adminPat: 0.3,
  ijmPat: 0,
  // Cotisations du canton (Genève : maternité, petite enfance… ; Vaud :
  // accueil de jour, PC Familles…) ; hors de Suisse, les charges sociales
  // du pays, estimées.
  cantonPat: 0,
  cantonSal: 0,
});

const somme = (liste) =>
  Math.round((liste || []).reduce((t, c) => t + (+c.taux || 0), 0) * 1000) / 1000;

/**
 * Ce qui fonde les taux par défaut d'une société : son canton en Suisse, son
 * pays ailleurs.
 *
 * <p>« Si c'est une société avec une adresse de Zurich, il doit se mettre
 * automatiquement avec les taux de Zurich. » L'AVS et le chômage sont les
 * mêmes dans toute la Suisse ; les allocations familiales et les cotisations
 * cantonales suivent le canton. Hors de Suisse, les cotisations suisses n'ont
 * pas cours : la société part des charges moyennes de son pays, une
 * estimation, que les réglages corrigent.
 *
 * @param {object} [soc] société
 * @returns {{pays: string, canton: ?string, nom: ?string, detailPat: object[],
 *   detailSal: object[], estimation: boolean, note: ?string}} l'origine des taux
 */
export function origineDesTaux(soc) {
  const pays = soc?.pays || 'CH';
  if (pays !== 'CH') {
    const p = CHARGES_SOCIALES.pays[pays];
    return {
      pays,
      canton: null,
      nom: null,
      detailPat: [],
      detailSal: [],
      estimation: true,
      connu: !!p,
      note: p?.note || null,
    };
  }
  const canton = cantonDeLaSociete(soc);
  const c = canton ? CHARGES_SOCIALES.cantons[canton] : null;
  return {
    pays,
    canton,
    nom: c?.nom || null,
    detailPat: c?.cantonPat || [],
    detailSal: [
      ...(c?.afSal ? [{ lib: 'Allocations familiales', taux: c.afSal }] : []),
      ...(c?.cantonSal || []),
    ],
    estimation: false,
    connu: !!c,
    note: c?.note || null,
  };
}

/**
 * Taux par défaut d'une société : le barème suisse et les taux de son canton,
 * ou, hors de Suisse, les charges estimées de son pays.
 *
 * @param {object} [soc] société
 * @returns {object} tous les taux, sous les clés de {@link TAUX_DEFAUT}
 */
export function tauxDefautDeLaSociete(soc) {
  const pays = soc?.pays || 'CH';
  if (pays !== 'CH') {
    const p = CHARGES_SOCIALES.pays[pays];
    const zero = Object.fromEntries(Object.keys(TAUX_DEFAUT).map((k) => [k, 0]));
    return { ...zero, cantonPat: +p?.pat || 0, cantonSal: +p?.sal || 0 };
  }
  const canton = cantonDeLaSociete(soc);
  const c = canton ? CHARGES_SOCIALES.cantons[canton] : null;
  if (!c) return { ...TAUX_DEFAUT };
  return {
    ...TAUX_DEFAUT,
    afPat: +c.afPat,
    cantonPat: somme(c.cantonPat),
    cantonSal: Math.round(((+c.afSal || 0) + somme(c.cantonSal)) * 1000) / 1000,
  };
}

/**
 * Année civile en cours : celle que les écrans proposent d'abord.
 *
 * @returns {number}
 */
export function anneeCourante() {
  return new Date().getFullYear();
}

const estObjet = (valeur) => valeur != null && typeof valeur === 'object' && !Array.isArray(valeur);

/**
 * Jeux de taux réglés par la société, par année, tels que saisis.
 *
 * <p>Un ancien réglage `soc.taux`, sans année, vaut pour l'année du barème ;
 * un jeu réglé pour cette même année sous le nouveau format l'emporte. Une
 * clé qui n'est pas une année, ou un jeu qui n'est pas un objet (réglage
 * illisible rendu tel quel par le mappeur), est ignorée.
 *
 * @param {object} [soc] société, telle que l'écran la détient
 * @returns {Object<string, object>} année → taux saisis
 */
export function tauxParAnnee(soc) {
  const resultat = {};
  if (estObjet(soc?.taux)) resultat[ANNEE_BAREME] = soc.taux;
  if (estObjet(soc?.tauxParAnnee)) {
    for (const [annee, taux] of Object.entries(soc.tauxParAnnee)) {
      if (/^\d{4}$/.test(annee) && estObjet(taux)) resultat[annee] = taux;
    }
  }
  return resultat;
}

/**
 * Années pour lesquelles la société a réglé des taux, de la plus récente à la
 * plus ancienne.
 *
 * @param {object} [soc] société
 * @returns {number[]}
 */
export function anneesReglees(soc) {
  return Object.keys(tauxParAnnee(soc))
    .map(Number)
    .sort((a, b) => b - a);
}

/**
 * Année dont les taux s'appliquent à une année donnée.
 *
 * <p>L'année elle-même si elle est réglée ; sinon la plus récente qui la
 * précède : des taux restent en vigueur tant que la société n'en règle pas
 * de nouveaux. Rien avant la première année réglée.
 *
 * @param {object} [soc]  société
 * @param {number} annee  année demandée
 * @returns {number|null} l'année dont les taux s'appliquent, ou `null`
 */
export function anneeApplicable(soc, annee) {
  const candidates = anneesReglees(soc).filter((a) => a <= annee);
  return candidates.length ? candidates[0] : null;
}

/** Les taux saisis qui s'appliquent à une année ; rien sans réglage. */
const saisisPour = (soc, annee) => {
  const source = anneeApplicable(soc, annee);
  return source == null ? {} : tauxParAnnee(soc)[source];
};

/**
 * Taux d'une année tels que l'entreprise les a saisis : le barème, corrigé de
 * ce qu'elle a réglé. C'est ce que l'éditeur affiche ; les calculs lisent
 * {@link tauxApplicables}, qui borne chaque taux.
 *
 * @param {object} [soc]   société, telle que l'écran la détient
 * @param {number} [annee] année de la période de paie ; l'année en cours à défaut
 * @returns {object} tous les taux, aucun manquant
 */
export function tauxCharges(soc, annee = anneeCourante()) {
  return { ...tauxDefautDeLaSociete(soc), ...saisisPour(soc, annee) };
}

/**
 * Taux d'une année après modification d'une seule ligne.
 *
 * <p>Un champ vidé vaut zéro : laisser passer une chaîne vide donnerait un
 * « NaN » qui se propagerait jusqu'au net à payer.
 *
 * @param {object} [soc]  société en cours d'édition
 * @param {number} annee  année modifiée
 * @param {string} cle    taux modifié
 * @param {string|number} valeur saisie de l'utilisateur
 * @returns {object} le jeu complet de l'année, avec la ligne modifiée
 */
export function tauxModifies(soc, annee, cle, valeur) {
  return { ...tauxCharges(soc, annee), [cle]: valeur === '' ? 0 : +valeur };
}

/**
 * La société, avec un jeu de taux pour une année.
 *
 * <p>L'ancien réglage sans année est repris dans le format par année puis
 * retiré : une fois copié, il n'a plus rien à dire, et le garder laisserait
 * deux réglages pour la même année.
 *
 * @param {object} [soc]  société
 * @param {number} annee  année réglée
 * @param {object} taux   jeu de taux de l'année
 * @returns {object} la société modifiée
 */
export function avecTauxDeLAnnee(soc, annee, taux) {
  return { ...soc, tauxParAnnee: { ...tauxParAnnee(soc), [annee]: taux }, taux: undefined };
}

/**
 * La société, avec l'année suivante préparée : elle reprend les taux en
 * vigueur l'année précédente, à corriger ensuite ligne à ligne.
 *
 * @param {object} [soc]  société
 * @param {number} annee  année à préparer
 * @returns {object} la société modifiée
 */
export function preparerAnnee(soc, annee) {
  return avecTauxDeLAnnee(soc, annee, tauxCharges(soc, annee - 1));
}

/**
 * Taux le plus élevé admis pour une charge, en % : au-delà, la saisie est
 * tenue pour une erreur (comme côté serveur, `TauxCotisations.MAXIMUM`).
 */
export const TAUX_MAXIMUM = 20;

/**
 * Plafond des cotisations du canton, et surtout des charges d'un pays hors de
 * Suisse, qui en additionnent plusieurs : jusqu'à 40 % et plus.
 */
export const TAUX_MAXIMUM_PAYS = 60;

/** Les taux qui portent le canton, ou le pays : leur propre plafond. */
const CLES_CANTON = new Set(['cantonPat', 'cantonSal']);

/**
 * Un taux réglé, s'il est admissible ; à défaut, la valeur par défaut.
 *
 * <p>Même règle que la fiche officielle du serveur (`TauxCotisations`) : un
 * taux absent, illisible, négatif ou supérieur à {@link TAUX_MAXIMUM} est
 * ignoré. Zéro est un taux. Sans cela, l'estimation et la fiche officielle
 * annonceraient deux nets différents pour une saisie absurde.
 *
 * @param {unknown} valeur taux saisi : nombre ou texte numérique
 * @param {number} defaut  taux rendu pour une saisie inadmissible
 * @param {number} [maximum] plafond admis ; {@link TAUX_MAXIMUM} à défaut
 * @returns {number} le taux, compris entre 0 et {@link TAUX_MAXIMUM}
 */
export function tauxValide(valeur, defaut, maximum = TAUX_MAXIMUM) {
  if (valeur == null || typeof valeur === 'boolean') return defaut;
  const texte = String(valeur).trim().replace(',', '.');
  if (texte === '') return defaut;
  const taux = Number(texte);
  if (!Number.isFinite(taux) || taux < 0 || taux > maximum) return defaut;
  return taux;
}

/**
 * Taux qu'appliquent les calculs de paie pour une année : ceux de
 * l'entreprise, chaque taux inadmissible remplacé par la valeur par défaut.
 *
 * <p>{@link tauxCharges} rend la saisie telle quelle, pour l'éditeur ; les
 * calculs lisent celle-ci, qui suit la règle de la fiche officielle.
 *
 * @param {object} [soc]   société, telle que l'écran la détient
 * @param {number} [annee] année de la période de paie ; l'année en cours à défaut
 * @returns {object} tous les taux, tous admissibles
 */
export function tauxApplicables(soc, annee = anneeCourante()) {
  const saisis = saisisPour(soc, annee);
  const sortie = {};
  for (const [cle, defaut] of Object.entries(tauxDefautDeLaSociete(soc))) {
    sortie[cle] = tauxValide(
      saisis[cle],
      defaut,
      CLES_CANTON.has(cle) ? TAUX_MAXIMUM_PAYS : TAUX_MAXIMUM,
    );
  }
  return sortie;
}

/**
 * Taux retenus sur le salaire, part employé, sous les clés de la fiche
 * (`retenuesSalariales`).
 *
 * @param {object} [soc]   société
 * @param {number} [annee] année de la période de paie ; l'année en cours à défaut
 * @returns {{avs: number, ac: number, aanp: number, ijm: number, canton: number}}
 */
export function tauxRetenues(soc, annee = anneeCourante()) {
  const t = tauxApplicables(soc, annee);
  return { avs: t.avsSal, ac: t.acSal, aanp: t.aanpSal, ijm: t.ijmSal, canton: t.cantonSal };
}

/** Libellé de la part de l'employé réglée par la société hors de Suisse (comme le serveur). */
export const LIBELLE_REGLE = 'Cotisations sociales de l’employé';

/**
 * Hors de Suisse, les cotisations du pays de la société, ligne par ligne :
 * nom officiel dans la langue du pays, part de l'employeur, part de l'employé.
 * La fiche de paie du serveur lit la même table (`TauxCotisationsSociete`).
 *
 * <p>Quand la société a réglé elle-même la part de l'employé, son taux
 * remplace les lignes côté employé, en une ligne ; la part de l'employeur
 * reste celle du pays.
 *
 * @param {object} [soc]   société
 * @param {number} [annee] année de la paie
 * @param {number} [cantonChoisi] part de l'employé réglée, quand l'écran ne lit pas les
 *   réglages de la société (espace de l'employé : le serveur la lui sert)
 * @returns {?Array<{libelle: string, libelleFr: string, pat: number, sal: number}>}
 *   les lignes du pays, `null` en Suisse
 */
export function lignesDuPays(soc, annee = anneeCourante(), cantonChoisi) {
  const pays = soc?.pays || 'CH';
  if (pays === 'CH') return null;
  const table = lignesReglees(soc, annee).filter((l) => l.pat > 0 || l.sal > 0);
  const choisi = +(cantonChoisi ?? tauxRetenues(soc, annee).canton) || 0;
  const duPays = table.reduce((a, l) => a + l.sal, 0);
  if (table.length && Math.abs(choisi - duPays) <= 0.001) return table;
  const sortie = table.filter((l) => l.pat > 0).map((l) => ({ ...l, sal: 0 }));
  if (choisi > 0)
    sortie.push({ libelle: LIBELLE_REGLE, libelleFr: LIBELLE_REGLE, pat: 0, sal: choisi });
  return sortie;
}

/**
 * Hors de Suisse, les lignes du pays telles que la société les a réglées pour
 * une année : le taux de la table, corrigé ligne par ligne dans les réglages.
 *
 * <p>Les corrections s'écrivent sous `lignesPays`, par année et par nom de
 * ligne : `{ "2026": { "Assurance maladie": { pat: 13, sal: 0 } } }`. Comme les
 * autres taux, celles d'une année valent pour les suivantes jusqu'à la
 * prochaine correction. Une part illisible garde le taux du pays.
 *
 * @param {object} [soc]   société
 * @param {number} [annee] année de la paie
 * @returns {Array<{libelle: string, libelleFr: string, pat: number, sal: number, base: string|null,
 *   patPays: number, salPays: number}>} toutes les lignes du pays, même à zéro
 */
export function lignesReglees(soc, annee = anneeCourante()) {
  const pays = soc?.pays || 'CH';
  const reglees = correctionsDesLignes(soc, annee);
  return (CHARGES_SOCIALES.pays[pays]?.lignes || []).map((l) => {
    const fr = l.fr || l.local;
    const c = reglees[fr] || {};
    const patPays = +l.pat || 0;
    const salPays = +l.sal || 0;
    return {
      libelle: l.local || l.fr,
      libelleFr: fr,
      base: l.base || null,
      pat: tauxValide(c.pat, patPays, TAUX_MAXIMUM_PAYS),
      sal: tauxValide(c.sal, salPays, TAUX_MAXIMUM_PAYS),
      patPays,
      salPays,
    };
  });
}

/** Les corrections de lignes qui valent pour une année : celles de la dernière année réglée avant elle. */
function correctionsDesLignes(soc, annee) {
  const parAnnee = estObjet(soc?.lignesPays) ? soc.lignesPays : {};
  const reglees = Object.keys(parAnnee)
    .map(Number)
    .filter((a) => Number.isInteger(a) && a <= annee)
    .sort((a, b) => b - a);
  return reglees.length && estObjet(parAnnee[reglees[0]]) ? parAnnee[reglees[0]] : {};
}

const arrondi3 = (x) => Math.round(x * 1000) / 1000;

/**
 * La société, avec une part d'une ligne du pays corrigée pour une année. Les
 * totaux du pays (`cantonPat`, `cantonSal`) suivent la somme des lignes : la
 * comptabilité et l'aperçu les lisent.
 *
 * @param {object} [soc]  société
 * @param {number} annee  année réglée
 * @param {string} nom    nom français de la ligne
 * @param {'pat'|'sal'} part part corrigée
 * @param {string|number} valeur saisie ; vide vaut zéro
 * @returns {object} la société modifiée
 */
export function avecLigneDuPays(soc, annee, nom, part, valeur) {
  const actuelles = Object.fromEntries(
    lignesReglees(soc, annee).map((l) => [l.libelleFr, { pat: l.pat, sal: l.sal }]),
  );
  actuelles[nom] = { ...actuelles[nom], [part]: valeur === '' ? 0 : +valeur };
  const avecLignes = { ...soc, lignesPays: { ...(soc?.lignesPays || {}), [annee]: actuelles } };
  const lignes = lignesReglees(avecLignes, annee);
  const taux = {
    ...tauxCharges(avecLignes, annee),
    cantonPat: arrondi3(lignes.reduce((a, l) => a + l.pat, 0)),
    cantonSal: arrondi3(lignes.reduce((a, l) => a + l.sal, 0)),
  };
  return avecTauxDeLAnnee(avecLignes, annee, taux);
}

/**
 * La société, revenue au barème par défaut pour une année : taux et lignes du
 * pays. Les corrections d'une année passée ne s'y appliquent plus.
 *
 * @param {object} [soc]  société
 * @param {number} annee  année remise à zéro
 * @returns {object} la société modifiée
 */
export function avecBaremeParDefaut(soc, annee) {
  const avecTaux = avecTauxDeLAnnee(soc, annee, tauxDefautDeLaSociete(soc));
  if (!estObjet(soc?.lignesPays)) return avecTaux;
  return { ...avecTaux, lignesPays: { ...soc.lignesPays, [annee]: {} } };
}

/**
 * Taux propres à la comptabilité, absents du barème de la paie : LPP part
 * employeur (moyenne), CCT / Parifonds, RETABAT et impôts de la société.
 *
 * <p>Les autres charges de la comptabilité — AVS, AC, LAA, allocations
 * familiales, frais d'administration, IJM — sont celles du barème de la paie
 * de l'année (voir {@link CHARGES_COMPTA_DANS_BAREME}), qui se règle dans les
 * réglages. La comptabilité tenait son propre jeu de dix taux, jamais
 * enregistré, et qui divergeait de celui de l'équipe : la paie comptait
 * l'accident à 0,7 % quand le bilan le comptait à 0,8 %.
 */
export const TAUX_COMPTA_DEFAUT = Object.freeze({
  lpp: 6,
  cct: 1,
  retabat: 0.7,
  impotCapital: 0.2,
  impotSociete: 14,
  // Le patron indépendant : AVS/AI/APG sur son bénéfice, puis l'impôt sur
  // le revenu (Confédération, canton, commune), taux moyen.
  avsIndependant: 10,
  impotRevenu: 15,
});

/** Charges de la comptabilité qui se lisent dans le barème de la paie. */
export const CHARGES_COMPTA_DANS_BAREME = Object.freeze({
  avs: 'avsPat',
  ac: 'acPat',
  laa: 'aapPat',
  af: 'afPat',
  admin: 'adminPat',
  ijm: 'ijmPat',
  canton: 'cantonPat',
});

const tauxPositif = (valeur, defaut) => {
  if (valeur == null || valeur === '' || typeof valeur === 'boolean') return defaut;
  const taux = Number(String(valeur).trim().replace(',', '.'));
  return Number.isFinite(taux) && taux >= 0 ? taux : defaut;
};

/**
 * Les treize taux qu’applique la comptabilité : part employeur du barème de la
 * paie de l'année pour AVS, AC, LAA, AF, frais d'administration et IJM ;
 * réglages propres pour le reste.
 *
 * @param {object} [soc]   société, telle que l'écran la détient
 * @param {number} [annee] exercice ; l'année en cours à défaut
 * @returns {{avs: number, ac: number, laa: number, af: number, admin: number, ijm: number,
 *   lpp: number, cct: number, retabat: number, impotCapital: number, impotSociete: number,
 *   avsIndependant: number, impotRevenu: number}}
 */
export function tauxCompta(soc, annee = anneeCourante()) {
  const bareme = tauxApplicables(soc, annee);
  const propres = (soc && soc.tauxCompta) || {};
  const sortie = {};
  for (const [cle, cleBareme] of Object.entries(CHARGES_COMPTA_DANS_BAREME)) {
    sortie[cle] = bareme[cleBareme];
  }
  // La LPP, la CCT et la retraite anticipée du bâtiment sont suisses : hors
  // de Suisse, rien par défaut ; les charges du pays sont dans le barème.
  const suisse = (soc?.pays || 'CH') === 'CH';
  for (const [cle, defaut] of Object.entries(TAUX_COMPTA_DEFAUT)) {
    const d = !suisse && ['lpp', 'cct', 'retabat'].includes(cle) ? 0 : defaut;
    sortie[cle] = tauxPositif(propres[cle], d);
  }
  return sortie;
}

/**
 * Le total des charges sociales patronales, en pour-cent du salaire brut :
 * AVS, AC, LAA, IJM, frais d'administration, LPP et allocations familiales,
 * plus, dans le bâtiment, la CCT et la retraite anticipée. La vue complète et
 * la synthèse de la comptabilité additionnent les mêmes.
 *
 * @param {object} TX taux de {@link tauxCompta}
 * @param {boolean} [batiment] société du bâtiment (CCT, retraite anticipée)
 * @returns {number} le total, en pour-cent
 */
export function tauxPatronauxTotal(TX, batiment = false) {
  const n = (v) => +v || 0;
  return (
    n(TX.avs) +
    n(TX.ac) +
    n(TX.laa) +
    n(TX.ijm) +
    n(TX.admin) +
    n(TX.lpp) +
    n(TX.af) +
    n(TX.canton) +
    (batiment ? n(TX.cct) + n(TX.retabat) : 0)
  );
}

/**
 * Ce qu'il faut écrire dans la société quand la comptabilité modifie un de
 * ses taux propres ({@code soc.tauxCompta}). Les charges du barème ne se
 * modifient que dans les réglages, par année. Un champ vidé vaut zéro, jamais
 * un NaN.
 *
 * @param {object} [soc]  société en cours d'édition
 * @param {string} cle    taux modifié, clé de {@link TAUX_COMPTA_DEFAUT}
 * @param {string|number} valeur saisie de l'utilisateur
 * @returns {object} le champ de la société à remplacer
 */
export function tauxComptaModifies(soc, cle, valeur) {
  const avant = { ...((soc && soc.tauxCompta) || {}) };
  const nombre = valeur === '' || valeur == null ? NaN : Number(String(valeur).replace(',', '.'));
  // Une case vidée, un texte ou un taux hors de 0 à 100 % ne s'enregistrent
  // pas : le taux en place reste. Ils valaient 0 %, et l'estimation des
  // impôts tombait à zéro sur une faute de frappe.
  if (!Number.isFinite(nombre) || nombre < 0 || nombre > 100) return { tauxCompta: avant };
  return { tauxCompta: { ...avant, [cle]: nombre } };
}

/**
 * Le taux par défaut de chaque taux propre à la comptabilité, pour la
 * société : hors de Suisse, ni LPP, ni CCT, ni retraite anticipée du bâtiment.
 *
 * @param {object} [soc] société
 * @returns {object} les taux par défaut, par clé
 */
export function tauxComptaParDefaut(soc) {
  const suisse = (soc?.pays || 'CH') === 'CH';
  return Object.fromEntries(
    Object.entries(TAUX_COMPTA_DEFAUT).map(([cle, d]) => [
      cle,
      !suisse && ['lpp', 'cct', 'retabat'].includes(cle) ? 0 : d,
    ]),
  );
}

/**
 * La société dont un taux de la comptabilité revient au défaut — ou tous,
 * sans clé : le réglage propre est retiré, le défaut reprend.
 *
 * @param {object} [soc] société
 * @param {string} [cle] taux à remettre ; tous s'il manque
 * @returns {{tauxCompta: object}} le réglage à fusionner dans la société
 */
export function tauxComptaRemis(soc, cle) {
  if (!cle) return { tauxCompta: {} };
  const { [cle]: _retire, ...reste } = (soc && soc.tauxCompta) || {};
  return { tauxCompta: reste };
}

/**
 * Au-delà de ces taux, la comptabilité signale une erreur de frappe probable
 * (« 150 » pour « 15 »), sans refuser : un canton cher ou une caisse
 * particulière peut les dépasser.
 */
export const TAUX_COMPTA_INHABITUELS = Object.freeze({
  lpp: 20,
  cct: 5,
  retabat: 5,
  impotCapital: 2,
  impotSociete: 30,
  avsIndependant: 11,
  impotRevenu: 45,
});

/** Taux AVS/AI/APG d'un indépendant au-dessus de la limite supérieure. */
export const BAREME_AVS_TAUX_MAX = 10;

/**
 * Les limites du barème dégressif des indépendants (OFAS) : 2025-2026,
 * 10'100.– à 60'500.– ; dès 2027, 10'300.– à 61'500.–.
 *
 * @param {number} annee exercice
 * @returns {{bas: number, haut: number, tauxBas: number, minimum: number}} les limites
 */
export function baremeAvs(annee) {
  return annee >= 2027
    ? { bas: 10300, haut: 61500, tauxBas: 5.371, minimum: 530 }
    : { bas: 10100, haut: 60500, tauxBas: 5.371, minimum: 530 };
}

/**
 * La cotisation AVS/AI/APG d'un indépendant sur son revenu de l'année :
 * la cotisation minimale sous la limite inférieure, un taux qui monte de
 * 5.371 % à 10 % entre les deux limites (le barème officiel monte par
 * paliers ; l'estimation suit la droite), 10 % au-delà. Les frais
 * d'administration de la caisse ne sont pas compris.
 *
 * @param {number} revenu revenu de l'année (bénéfice avant AVS)
 * @param {number} annee exercice
 * @returns {number} la cotisation de l'année
 */
export function avsIndependantAnnuel(revenu, annee) {
  const { bas, haut, tauxBas, minimum } = baremeAvs(annee);
  const r = Math.max(0, +revenu || 0);
  if (r < bas) return minimum;
  if (r >= haut) return (r * BAREME_AVS_TAUX_MAX) / 100;
  const taux = tauxBas + ((BAREME_AVS_TAUX_MAX - tauxBas) * (r - bas)) / (haut - bas);
  return Math.max(minimum, (r * taux) / 100);
}

/**
 * Les impôts à mettre de côté sur un bénéfice, selon la forme de la société.
 *
 * <p>L'indépendant paie l'AVS/AI/APG sur son bénéfice, puis l'impôt sur le
 * revenu sur ce qui reste (les cotisations se déduisent du revenu). Une Sàrl
 * ou une SA paie l'impôt sur le bénéfice et l'impôt sur le capital ; le salaire
 * du directeur est déjà une dépense, et son impôt personnel n'est pas compté.
 * Ce sont des estimations à taux moyens, réglables dans la comptabilité.
 *
 * @param {object} p
 * @param {number} p.benefice bénéfice de la période, hors TVA
 * @param {boolean} p.independant la société est une raison individuelle
 * @param {object} p.TX taux de {@link tauxCompta}
 * @param {number} [p.capital] capital social (Sàrl, SA)
 * @param {number} [p.partAnnee] part de l'année couverte (impôt sur le capital,
 *   barème de l'AVS calculé sur le revenu de l'année)
 * @param {number} [p.annee] exercice, pour les limites du barème de l'AVS
 * @returns {{independant: boolean, avs: number, revenu: number, societe: number,
 *   capital: number, total: number}} les montants, arrondis au centime
 */
export function impotsAProvisionner({
  benefice,
  independant,
  TX,
  capital = 0,
  partAnnee = 1,
  annee = new Date().getFullYear(),
}) {
  const base = Math.max(0, +benefice || 0);
  const cts = (v) => Math.round(v * 100) / 100;
  if (independant) {
    // Au taux légal (10 %), le barème dégressif et la cotisation minimale de
    // l'AVS, calculés sur le revenu de l'année ; un taux réglé à la main dans
    // la Compta reste appliqué tel quel.
    const legal = +TX.avsIndependant === BAREME_AVS_TAUX_MAX;
    const part = +partAnnee > 0 ? +partAnnee : 1;
    const avs = legal
      ? cts(avsIndependantAnnuel(base / part, annee) * part)
      : cts((base * (+TX.avsIndependant || 0)) / 100);
    const revenu = cts((Math.max(0, base - avs) * (+TX.impotRevenu || 0)) / 100);
    const avsMinimum = legal && base / part < baremeAvs(annee).bas;
    return {
      independant,
      avs,
      revenu,
      societe: 0,
      capital: 0,
      total: cts(avs + revenu),
      base,
      avsMinimum,
    };
  }
  const societe = cts((base * (+TX.impotSociete || 0)) / 100);
  const surCapital = cts((((+capital || 0) * (+TX.impotCapital || 0)) / 100) * partAnnee);
  return {
    independant: false,
    avs: 0,
    revenu: 0,
    societe,
    capital: surCapital,
    total: cts(societe + surCapital),
  };
}
